
import { defineComponent, getCurrentInstance, onMounted } from 'vue';
import HeaderComponent from './landing-sections/Header.vue';
import FooterComponent from '../docs-main/Footer.vue';
import SearchInput from '@/components/docs-main/search/SearchInput.vue';
import { StoreActions, StoreMutations } from '@/templates/DataNames';
import { parseHrefHelper } from '@/heplers/stringHelpers';
import type { GtmEvent, GtmMarked } from '@/plugins/gdprPlugin';

enum TutorialsFilter {
  VOICE = 'Voice',
  VIDEO = 'Video',
  MESSAGING = 'Messaging',
}

interface ITutorialLinkObject {
  link?: string;
  name?: string;
  imageUrl?: string;
  isDocLink?: boolean;
}

interface ITutorialsList extends GtmMarked {
  list: ITutorialLinkObject[];
}

const filteredTutorials: Record<TutorialsFilter, ITutorialsList> = {
  Voice: {
    gtm: {
      event: 'vox_docs_to_voice_docs',
      category: 'docs_links',
      action: 'voice_docs',
    },
    list: [
      { name: 'Voice calls', link: 'guides.calls.audio' },
      { name: 'Speech synthesis', link: 'guides.speech.tts' },
      { name: 'Speech recognition', link: 'guides.speech.stt' },
    ],
  },
  Video: {
    gtm: {
      event: 'vox_docs_to_video_docs',
      category: 'docs_links',
      action: 'video_docs',
    },
    list: [
      { name: 'Video calls', link: 'guides.calls.video' },
      { name: 'Video conferences', link: 'guides.conferences.howto' },
      { name: 'Video recording', link: 'guides.calls.record' },
    ],
  },
  Messaging: {
    gtm: {
      event: 'vox_docs_to_messaging_docs',
      category: 'docs_links',
      action: 'messaging_docs',
    },
    list: [{ name: 'SMS', link: 'guides.sms' }],
  },
};

const mediaSdks: ITutorialsList = {
  gtm: {
    event: 'vox_docs_to_media_sdk_docs',
    category: 'docs_links',
    action: 'media_sdk_docs',
  },
  list: [
    { name: 'IOS', link: 'references.iossdk', imageUrl: 'ios' },
    { name: 'Android', link: 'references.androidsdk', imageUrl: 'android' },
    { name: 'React Native', link: 'references.reactnative', imageUrl: 'react-native' },
    { name: 'Web', link: 'references.websdk', imageUrl: 'websdk' },
    { name: 'Flutter', link: 'references.fluttersdk', imageUrl: 'flutter' },
  ],
};

const apiClients: ITutorialsList = {
  gtm: {
    event: 'vox_docs_to_api_clients_docs',
    category: 'docs_links',
    action: 'api_clients_docs',
  },
  list: [
    { name: 'Curl', imageUrl: 'curl', isDocLink: true, link: 'references.httpapi' },
    {
      name: 'Node.js',
      imageUrl: 'node',
      link: 'https://www.npmjs.com/package/@voximplant/apiclient-nodejs',
    },
    { name: 'Python', imageUrl: 'python', link: 'https://pypi.org/project/voximplant-apiclient/' },
    {
      name: 'PHP',
      imageUrl: 'php',
      link: 'https://packagist.org/packages/voximplant/apiclient-php',
    },
    { name: 'Go', imageUrl: 'go', link: 'https://github.com/voximplant/apiclient-go' },
    { name: 'Java', imageUrl: 'java', link: 'https://github.com/voximplant/apiclient-java' },
    { name: 'C#', imageUrl: 'csharp', link: 'https://github.com/voximplant/apiclient-dotnet' },
  ],
};

const sdkTools: ITutorialsList = {
  gtm: {
    event: 'vox_docs_to_tools_docs',
    category: 'docs_links',
    action: 'tools_docs',
  },
  list: [
    { name: 'Cloud IDE', imageUrl: 'ide', link: 'guides.voxengine.ide' },
    { name: 'Debugger', imageUrl: 'debugger', link: 'guides.troubleshooting.scenarios' },
  ],
};

export default defineComponent({
  name: 'Landing',
  components: {
    SearchInput,
    HeaderComponent,
    FooterComponent,
  },
  metaInfo() {
    return {
      title:
        typeof window === 'undefined' ? 'Voximplant Documentation' : 'Voximplant Documentation',
      script: [
        {
          type: 'application/ld+json',
          json: {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            url: 'https://voximplant.com/docs/',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://voximplant.com/docs/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          },
        },
      ],
    };
  },
  setup() {
    const instance = getCurrentInstance();
    const proxy = instance?.proxy as
      | {
          $router?: { push: (p: string) => Promise<unknown>; currentRoute: { fullPath: string } };
          $sendGtmEvent?: (payload: GtmEvent) => void;
          $localStorageSet?: (key: string, value: string) => void;
          $store?: {
            dispatch: (action: string, payload?: unknown) => Promise<unknown>;
            commit: (mutation: string, payload?: unknown) => void;
          };
        }
      | undefined;

    const router = proxy?.$router;
    const sendGtmEventToProxy = proxy?.$sendGtmEvent;
    const localStorageSet = proxy?.$localStorageSet;
    const store = proxy?.$store;

    const parseHref = parseHrefHelper;

    function sendGtmEvent(event: GtmEvent, label?: string) {
      sendGtmEventToProxy?.({ ...event, label });
    }

    function goToLink(fqdn: string, getStarted?: boolean) {
      if (getStarted) {
        sendGtmEvent({
          event: 'site_main_docs_get_started',
          category: 'main_docs',
          action: 'get_started',
        });
      }

      try {
        localStorageSet?.('fromLanding', 'true');
      } catch (error) {
        console.error('Failed to set item to localStorage', error);
      }

      store
        ?.dispatch(StoreActions.CONTENT, { fqdn })
        .then(() => router?.push(parseHref(fqdn)).catch((error: unknown) => error))
        .catch((error: unknown) => error);
    }

    const setLanguage = (lang: string) => {
      store?.commit(StoreMutations.UPDATE_LANGUAGES_PACK, lang);
    };

    const getStartedClickHandler = () => {
      sendGtmEvent({
        event: 'vox_docs_to_getting_started',
        category: 'main_docs',
        action: 'get_started',
      });
      goToLink('getting-started', true);
    };

    const serverlessClickHandler = () => {
      sendGtmEvent({
        event: 'vox_docs_to_ve_docs',
        category: 'docs_links',
        action: 've_docs',
      });
      goToLink('references.voxengine');
    };

    const toolClickHandler = (gtm: GtmEvent, tool: ITutorialLinkObject) => {
      sendGtmEvent(gtm, tool.name?.toLowerCase());
      if (tool.link) goToLink(tool.link);
    };

    const onMediaSdkClick = (sdk: ITutorialLinkObject) => {
      sendGtmEvent(mediaSdks.gtm!, sdk.name);
      if (sdk.link) goToLink(sdk.link);
    };

    const goToHttpapiAndSetLang = (apiTitle: string) => {
      const prepareLang = apiTitle.replace(/\./, '').toLowerCase();
      setLanguage(prepareLang);
      goToLink('references.httpapi');
    };

    const onApiDocLinkClick = (api: ITutorialLinkObject) => {
      sendGtmEvent(apiClients.gtm!, api.name);
      if (api.name) goToHttpapiAndSetLang(api.name);
    };

    onMounted(() => {
      sendGtmEvent(
        {
          event: 'vox_docs_open',
          category: 'main_docs',
          action: 'open',
        },
        document.referrer
      );
      store?.commit(StoreMutations.CONTENT, []);
    });

    return {
      filteredTutorials,
      mediaSdks,
      apiClients,
      sdkTools,
      parseHref,
      sendGtmEvent,
      getStartedClickHandler,
      serverlessClickHandler,
      toolClickHandler,
      onMediaSdkClick,
      onApiDocLinkClick,
    };
  },
});
